import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { useDispatch, useSelector } from 'react-redux';

import {
  addNewUsers,
  changeStatusChat,
  createChatTopic,
  deleteChatGroupById,
  getMiniGroups,
  getUsersInGroup,
  updateChatGroup,
} from '@/store/chat/actions';
import { createFileImage } from '@/store/files/actions';

import { selectChat } from '@/store/chat/selectors';
import { selectUsers } from '@/store/users/selectors';

import { Avatar, Button, Form, message, Modal, Popconfirm, Popover } from 'antd';
import { DeleteOutlined, EditOutlined, PlusCircleOutlined, PoweroffOutlined } from '@ant-design/icons';
import { FiEdit2, FiUserPlus, FiUsers } from 'react-icons/fi';

import NewMiniGroup from '../../ChatsList/components/NewMiniGroup';
import TitleChat from '../../ChatsList/components/TitleChat';
import UsersList from '../../ChatsList/components/UsersList';
import ChatUser from './ChatUser';
import MiniGroupElement from './MiniGroupElement';

import useImage from '@shared/hooks/useImage';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import styles from '../Messages.module.scss';

const RENDER = {
  INFO: 'INFO',
  USERS: 'USERS',
  EDIT: 'EDIT',
  CREATE: 'CREATE',
  GROUP: 'GROUP',
};

const ChatModal = ({ visible, onClose, isTopic }) => {
  const { t } = useTranslation('chat');
  const { selectedUser, chatUsers, selectedChatTopic, chatMiniGroups, pagination, isLoading } = useSelector(selectChat);
  const { currentUser } = useSelector(selectUsers);

  const [render, setRender] = useState(RENDER.INFO);
  const [selectedUserState, setSelectedUserState] = useState([]);
  const [selectedMiniGroup, setSelectedMiniGroup] = useState(null);
  const [chatInfo, setChatInfo] = useState({
    chatName: undefined,
    chatPhoto: null,
    chatUsersList: [],
  });
  const [chatPhotoBlob, setChatPhotoBlob] = useState(null);
  const [chatPhotoPreview, setChatPhotoPreview] = useState(null);

  const dispatch = useDispatch();
  const avatarChat = useImage(isTopic ? selectedChatTopic.photo : selectedUser.photo, 'avatar');
  const form = Form.useForm();

  const getChatUser = () => dispatch(getUsersInGroup(selectedUser?.chatId));
  const getChatMiniGroups = () => dispatch(getMiniGroups(selectedUser?.chatId));

  useEffect(() => {
    getChatUser();
    if (currentUser.id === selectedUser.authorId) getChatMiniGroups();
  }, [selectedUser]);

  useEffect(() => {
    const chatUsersList = chatUsers?.map(user => ({
      id: user.id,
      firstName: user.first_name,
      lastName: user.lastName,
    }));

    setChatInfo({
      chatName: isTopic ? selectedChatTopic.chatName : selectedUser.chatName,
      chatPhoto: isTopic ? selectedChatTopic.photo : selectedUser.photo,
      withBreakout: selectedChatTopic?.withBreakout,
      hasTopic: isTopic ? false : selectedUser.hasTopic,
      chatUsersList,
    });
  }, [selectedUser, chatUsers, selectedChatTopic]);

  const handleDeleteGroup = () => {
    dispatch(deleteChatGroupById(isTopic ? selectedChatTopic.chatId : selectedUser.chatId), () => {
      message.success(t('deleteGroupSuccess'));
    });
  };

  const handleCancelButton = () => {
    setRender(RENDER.INFO);
  };

  const handleOkButton = newData => {
    if (render === RENDER.USERS) {
      setRender(RENDER.INFO);
      setSelectedUserState([]);
      dispatch(
        addNewUsers(
          selectedUser.chatId,
          selectedUserState?.map(user => user.id)
        )
      );
    }

    if (render === RENDER.EDIT) {
      const updateGroup = res => {
        dispatch(
          updateChatGroup(isTopic ? selectedChatTopic.chatId : selectedUser.chatId, {
            chatName: newData.chatName,
            chatPhotoUuid: res?.uuid ?? chatInfo.chatPhoto,
            withBreakout: newData.withBreakout,
          })
        );

        setRender(RENDER.INFO);
      };

      if (chatPhotoBlob) {
        const formData = new FormData();
        formData.append('file', chatPhotoBlob);

        dispatch(createFileImage(formData, res => updateGroup(res)));
      } else {
        updateGroup();
      }
    }
  };

  const renderTitle = () => {
    switch (render) {
      case RENDER.INFO:
        return t('titleChat.0');
      case RENDER.EDIT:
        return t('titleChat.2');
      case RENDER.USERS:
        return t('titleChat.1');
      case RENDER.GROUP:
        if (selectedMiniGroup) return t('titleChat.5');
        return t('titleChat.3');
      case RENDER.CREATE:
        return t('titleChat.4');
    }
  };

  const renderFooter = () => {
    if (render === RENDER.USERS) {
      return (
        <div className={styles.groupModal__footer}>
          <Button className={styles.groupModal__popoverElement} onClick={handleCancelButton} ghost>
            {t('cancel')}
          </Button>
          <Button
            disabled={!selectedUserState?.length}
            onClick={handleOkButton}
            className={styles.groupModal__popoverElement}
            type='primary'
          >
            {t('ok')}
          </Button>
        </div>
      );
    }

    return null;
  };

  const handleCreateChatTopic = data => {
    dispatch(createChatTopic(selectedUser.chatId, data));
    handleCloseModal();
  };

  const handleSelectUser = newUser => {
    if (selectedUserState.filter(user => user.id === newUser.id)?.length) {
      setSelectedUserState(selectedUserState.filter(user => user.id !== newUser.id));
    } else {
      setSelectedUserState(prev => [...prev, newUser]);
    }
  };

  const updateChatPhoto = blob => {
    setChatPhotoBlob(blob);
    setChatPhotoPreview(URL.createObjectURL(blob));
  };

  const handleChangeStatus = () => {
    dispatch(changeStatusChat(selectedUser.chatId, !chatInfo.hasTopic));
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setRender(RENDER.INFO);
    onClose();
  };

  const contentPopover = (
    <>
      <div className={styles.groupModal__popoverElement} onClick={() => setRender(RENDER.EDIT)}>
        <EditOutlined />
        <p>{t('editGroup.0')}</p>
      </div>
      {!isTopic && selectedUser?.hasTopic && (
        <div className={styles.groupModal__popoverElement} onClick={() => setRender(RENDER.CREATE)}>
          <PlusCircleOutlined />
          <p>{t('addTopic')}</p>
        </div>
      )}
      {!isTopic && selectedUser?.trackId && (
        <div className={styles.groupModal__popoverElement} onClick={() => setRender(RENDER.GROUP)}>
          <PlusCircleOutlined />
          <p>{t('createMiniGroup')}</p>
        </div>
      )}
      {!isTopic && (
        <div className={styles.groupModal__popoverElement} onClick={handleChangeStatus}>
          <PoweroffOutlined />
          <p>{t(chatInfo?.hasTopic ? 'chatTopic.1' : 'chatTopic.0')}</p>
        </div>
      )}
      {(isTopic && selectedChatTopic?.canDelete) || (!isTopic && selectedUser?.canDelete && !selectedUser?.trackId) ? (
        <Popconfirm
          className={classNames(styles.groupModal__popoverElement, styles.groupModal__popoverElement_red)}
          title={t('deleteGroup')}
          okText={t('yes')}
          cancelText={t('no')}
          onConfirm={handleDeleteGroup}
        >
          <DeleteOutlined />
          <p>{t('editGroup.1')}</p>
        </Popconfirm>
      ) : null}
    </>
  );

  const onSelectMiniGroup = group => {
    setSelectedMiniGroup(group);
    setRender(RENDER.GROUP);
  };

  const onSubmitMiniGroup = () => {
    getChatUser();
    getChatMiniGroups();
    setSelectedMiniGroup(null);
    setRender(RENDER.INFO);
  };

  const onCancelMiniGroup = () => {
    setSelectedMiniGroup(null);
    setRender(RENDER.INFO);
  };

  const loadMore = () => {
    dispatch(getUsersInGroup(selectedUser?.chatId, pagination.getChatUsers.page + 1));
  };

  const renderBlock = () => {
    if (render === RENDER.INFO) {
      return (
        <div className={styles.groupModal}>
          <div className={styles.groupModal__header}>
            <div className={styles.groupModal__block}>
              {!isTopic && <Avatar src={avatarChat} size={50} />}
              <div className={styles.groupModal__box}>
                <p className={styles.groupModal__name}>
                  {isTopic ? selectedChatTopic.chatName : selectedUser.chatName}
                </p>
                {!isTopic && (
                  <p className={styles.groupModal__countUsers}>
                    {selectedUser?.countChatUsers} {t('users')}
                  </p>
                )}
              </div>
            </div>
            {currentUser?.id === selectedUser?.authorId && (
              <Popover trigger='click' content={contentPopover} placement='rightBottom'>
                <FiEdit2 className={styles.groupModal__icon} size={16} />
              </Popover>
            )}
          </div>
          <div className={styles['groupModal__mini-groups']}>
            {!isTopic &&
              chatMiniGroups?.map((group, index) => (
                <MiniGroupElement key={index} onSelect={() => onSelectMiniGroup(group)} {...group} />
              ))}
          </div>
          {!isTopic && (
            <div className={styles.groupModal__element}>
              <div className={styles.groupModal__container}>
                <FiUsers size={20} />
                <p>
                  {selectedUser?.countChatUsers} {t('users')}
                </p>
              </div>
              {currentUser?.id === selectedUser?.authorId && !selectedUser?.trackId && (
                <FiUserPlus size={20} onClick={() => setRender(RENDER.USERS)} className={styles.groupModal__icon} />
              )}
            </div>
          )}
          {!isTopic && (
            <div className={styles.groupModal__users}>
              <InfiniteScroll
                initialLoad={false}
                loadMore={loadMore}
                hasMore={chatUsers?.length < pagination.getChatUsers.totalElements && !isLoading.getChatUsers}
                useWindow={false}
              >
                {chatUsers?.map(user => (
                  <ChatUser key={user.id} chatId={selectedUser.chatId} data={user} canDelete={!selectedUser?.trackId} />
                ))}
              </InfiniteScroll>
            </div>
          )}
        </div>
      );
    }

    if (render === RENDER.EDIT) {
      return (
        <TitleChat
          form={form}
          name={chatInfo.chatName}
          withBreakout={chatInfo.withBreakout}
          imagePreview={chatPhotoPreview ?? avatarChat}
          updateImage={updateChatPhoto}
          handleCancelButton={() => setRender(RENDER.INFO)}
          onSubmitForm={handleOkButton}
          isTopic={isTopic}
        />
      );
    }

    if (render === RENDER.CREATE) {
      return (
        <TitleChat
          form={form}
          name={undefined}
          handleCancelButton={handleCloseModal}
          onSubmitForm={handleCreateChatTopic}
          isTopic
        />
      );
    }

    if (render === RENDER.GROUP) {
      return <NewMiniGroup onSubmit={onSubmitMiniGroup} onCancel={onCancelMiniGroup} group={selectedMiniGroup} />;
    }

    return (
      <UsersList
        defaultSelectedUser={chatInfo.chatUsersList}
        selectedUser={selectedUserState}
        onSelectUser={handleSelectUser}
      />
    );
  };

  return (
    <Modal open={visible} footer={renderFooter()} title={renderTitle()} onCancel={handleCloseModal} width={600}>
      {renderBlock()}
    </Modal>
  );
};

ChatModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func,
    validateFields: PropTypes.func,
  }),
};

export default memo(ChatModal);
